
import React, { useState, useCallback } from 'react';
import { UploadCloudIcon } from './icons/UploadCloudIcon';
import { AlertTriangleIcon } from './icons/AlertTriangleIcon';

interface FileUploadScreenProps {
  onFileUpload: (file: File) => void;
  error: string | null;
}

const FileUploadScreen: React.FC<FileUploadScreenProps> = ({ onFileUpload, error }) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleFileChange = (files: FileList | null) => {
    if (files && files.length > 0) {
      // PDF and DOCX are common, but Gemini can handle various formats.
      // We can add more specific client-side checks if needed.
      onFileUpload(files[0]);
    }
  };

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);
  
  const handleDragOver = useCallback((e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleFileChange(e.dataTransfer.files);
  }, [handleFileChange]);

  return (
    <div className="flex flex-col items-center justify-center pt-10">
      <div className="text-center max-w-2xl">
        <h2 className="text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
          Transform Your Resume for a Global Audience
        </h2>
        <p className="mt-6 text-lg leading-8 text-slate-600">
          Upload your resume in any language (PDF or image format), and our AI will analyze, translate, and optimize it into a professional, ATS-friendly English version in seconds.
        </p>
      </div>

      <div className="w-full max-w-xl mt-12">
        <input
          type="file"
          id="resume-upload"
          className="hidden"
          onChange={(e) => handleFileChange(e.target.files)}
          accept="application/pdf,image/jpeg,image/png,image/webp"
        />
        <label
          htmlFor="resume-upload"
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={`group cursor-pointer flex flex-col items-center justify-center w-full h-64 border-2 border-dashed rounded-lg transition-colors ${
            isDragging ? 'border-indigo-600 bg-indigo-50' : 'border-slate-300 hover:border-indigo-500 hover:bg-slate-50'
          }`}
        >
          <div className="flex flex-col items-center justify-center pt-5 pb-6 text-center">
            <UploadCloudIcon className={`w-12 h-12 mb-4 text-slate-400 group-hover:text-indigo-600 transition-colors ${isDragging ? 'text-indigo-600' : ''}`} />
            <p className="mb-2 text-sm text-slate-500">
              <span className="font-semibold text-indigo-600">Click to upload</span> or drag and drop
            </p>
            <p className="text-xs text-slate-500">PDF, PNG, JPG (MAX. 10MB)</p>
          </div>
        </label>
        {error && (
            <div className="mt-4 flex items-center p-3 text-sm text-red-800 rounded-lg bg-red-100" role="alert">
                <AlertTriangleIcon className="w-5 h-5 mr-2 flex-shrink-0"/>
                <span className="font-medium">Error:</span>&nbsp;{error}
            </div>
        )}
      </div>
    </div>
  );
};

export default FileUploadScreen;
